/**
 * Safely convert lamports (BigInt) to SOL (number)
 *
 * This function handles the conversion without losing precision by performing
 * the division in BigInt space first, then converting to Number.
 *
 * @param lamports - The amount in lamports as BigInt
 * @returns The amount in SOL as a number
 *
 * @example
 * lamportsToSol(2_000_000n) // Returns 0.002
 * lamportsToSol(2_000_000_000_000n) // Returns 2000.0
 */
export function lamportsToSol(lamports: bigint): number {
  // Convert to number first, then divide
  // This is safe for values up to ~9 quintillion lamports (~9 billion SOL)
  return Number(lamports) / 1_000_000_000;
}

/**
 * Convert lamports to SOL and format with specified decimal places
 *
 * @param lamports - The amount in lamports as BigInt
 * @param decimals - Number of decimal places (default: 4)
 * @returns Formatted SOL string
 *
 * @example
 * formatLamportsToSol(2_039_280n) // Returns "0.0020"
 * formatLamportsToSol(2_000_000_000n, 2) // Returns "2.00"
 */
export function formatLamportsToSol(lamports: bigint, decimals: number = 4): string {
  return lamportsToSol(lamports).toFixed(decimals);
}

/**
 * Format SOL balance by TRUNCATING instead of ROUNDING
 *
 * This prevents displaying more balance than the user actually has.
 * Critical for UX: users should never see "0.0010" when they only have 0.0009999.
 *
 * @param lamports - The amount in lamports as BigInt
 * @param decimals - Number of decimal places to display (default: 4)
 * @returns Formatted SOL string, truncated to avoid rounding up
 *
 * @example
 * formatSolBalance(999_999n) // Returns "0.0009" (not "0.0010")
 * formatSolBalance(1_000_000n) // Returns "0.0010"
 * formatSolBalance(1_234_567_890n) // Returns "1.2345"
 */
export function formatSolBalance(lamports: bigint, decimals: number = 4): string {
  // Convert lamports to the smallest unit at our display precision
  // For 4 decimals: multiply by 10^4 to get units of 0.0001 SOL
  const multiplier = 10 ** decimals;
  const lamportsPerSol = 1_000_000_000;

  // Calculate SOL value in our precision units (avoiding floating point)
  // Example: 999,999 lamports with 4 decimals:
  // (999,999 * 10,000) / 1,000,000,000 = 9.99999 units -> floor to 9 units -> 0.0009
  const solInSmallestUnit = Number(lamports * BigInt(multiplier)) / lamportsPerSol;

  // Truncate (floor) to ensure we never show more than user has
  const truncated = Math.floor(solInSmallestUnit) / multiplier;

  // Format with exact decimal places
  return truncated.toFixed(decimals);
}

/**
 * Convert SOL to lamports
 *
 * @param sol - The amount in SOL as a number or string
 * @returns The amount in lamports as BigInt
 *
 * @example
 * solToLamports(0.002) // Returns 2_000_000n
 * solToLamports(1.5) // Returns 1_500_000_000n
 * solToLamports("0.0001") // Returns 100_000n (precise!)
 */
export function solToLamports(sol: number | string): bigint {
  // Convert to string to avoid floating point errors
  const solStr = typeof sol === 'string' ? sol : sol.toString();

  // Split into integer and decimal parts
  const parts = solStr.split('.');
  const integerPart = parts[0] || '0';
  const decimalPart = (parts[1] || '').padEnd(9, '0').slice(0, 9); // SOL has 9 decimals

  // Combine and convert to BigInt
  const lamportsStr = integerPart + decimalPart;
  return BigInt(lamportsStr);
}
